import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { ControllerMessages } from 'src/intl/ControllerMessages';
import { GenericMessages } from 'src/intl/GenericMessages';
import { Organization } from '../../models/organization';
import { AppModal } from '../reusable-components/app-modal/AppModal';
import { InputSelect } from '../reusable-components/InputSelect';

interface ReassignControllerModalProps {
  /**
   * Whether the modal is open.
   */
  isOpen: boolean;
  /**
   * Triggers the request to close the modal and submit the edit-profile.
   */
  onSave: (orgId?: string) => void;
  /**
   * The unique identifier of the org the ctrl is currently assigned to.
   */
  controllerOrganizationId: string;
  /**
   * Organizations that the controller can be assigned to.
   */
  availableOrganizations: Organization[];
}

const ReassignControllerModal: React.FC<ReassignControllerModalProps> = ({
  isOpen,
  onSave,
  controllerOrganizationId,
  availableOrganizations,
}) => {
  const { formatMessage } = useIntl();
  const [updatedCtrlOrg, setUpdatedCtrlOrg] = useState<string | undefined>(controllerOrganizationId);

  useEffect(() => {
    setUpdatedCtrlOrg(controllerOrganizationId);
  }, [controllerOrganizationId, isOpen]);

  const handleCancel = React.useCallback(() => {
    onSave(undefined);
  }, [onSave]);

  return (
    <AppModal
      isOpen={isOpen}
      title={formatMessage(ControllerMessages.reassignControllerHeader)}
      onClose={handleCancel}
      primaryButton={{
        label: formatMessage(GenericMessages.saveButtonLabel),
        onClick: () => onSave(updatedCtrlOrg),
      }}
      secondaryButton={{
        label: formatMessage(GenericMessages.cancelButtonLabel),
        onClick: handleCancel,
      }}
    >
      <InputSelect<string>
        label={formatMessage(ControllerMessages.assignedOrganizationLabel)}
        value={updatedCtrlOrg}
        onChange={org => setUpdatedCtrlOrg(org)}
        placeholder={formatMessage(ControllerMessages.organizationPlaceholder)}
        options={availableOrganizations.map(org => ({
          value: org._id,
          label: org.orgName,
        }))}
        id="reassign-controller-select-organization"
      />
    </AppModal>
  );
};

export default ReassignControllerModal;
